"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { bPrimary, Empty, ErrorNote, field, OkNote, Spinner } from "./ui";
import { plural } from "@/lib/format/plural";

// «Объединить сорок мелких папок iiko в понятные категории» (решение владельца,
// 23.09) — касса несёт десятки узких категорий учёта, склад и меню KLAIT хотят
// несколько крупных. Слева — как категория называется В КАССЕ (с числом
// карточек), справа — куда она попадёт на складе KLAIT при следующей
// синхронизации каталога. По умолчанию пара не задана — значит, имя берётся
// как в кассе (см. applyCategoryMap на сервере).
//
// Общий компонент для десктопа (владелец/управляющий, ./api) и консоли
// оператора (opApi с ключом администратора) — только пара load/save отличает
// транспорт, вся логика экрана одна.

export type SeenCategory = {
  iikoCategory: string;
  count: number;
  /** Куда сейчас смотрит категория: своя пара в карте, иначе имя кассы как есть. */
  mappedTo: string;
};
export type CategoryMap = Readonly<Record<string, string>>;

type Result<T> = { ok: true; data: T } | { ok: false; error: string };

export default function IikoCategoryMapEditor({
  canEdit,
  klaitCategories,
  load,
  save,
}: {
  /** Есть ли право менять пары. Без него строки видны, поля выключены (владелец: «зона менеджер — read-only»). */
  canEdit: boolean;
  /** Уже заведённые категории склада KLAIT — подсказки комбобокса. */
  klaitCategories: readonly string[];
  load: () => Promise<Result<{ categories: SeenCategory[]; map: CategoryMap }>>;
  save: (patch: Record<string, string | null>) => Promise<Result<unknown>>;
}) {
  const [categories, setCategories] = useState<SeenCategory[] | null>(null);
  // Черновик правится локально, на сервер уходит только по «Сохранить» —
  // иначе каждая буква в поле слала бы запрос.
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const reload = useCallback(async () => {
    setErr("");
    const r = await load();
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setCategories(r.data.categories);
    setDraft(Object.fromEntries(r.data.categories.map((c) => [c.iikoCategory, c.mappedTo])));
  }, [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const dirty = useMemo(
    () => (categories ?? []).some((c) => (draft[c.iikoCategory] ?? c.mappedTo).trim() !== c.mappedTo),
    [categories, draft],
  );

  async function onSave() {
    if (!categories) return;
    const patch: Record<string, string | null> = {};
    for (const c of categories) {
      const next = (draft[c.iikoCategory] ?? c.mappedTo).trim();
      if (next === c.mappedTo) continue;
      // Совпало с собственным именем кассы — то же самое, что снять пару:
      // applyCategoryMap и без записи в карте отдаст имя кассы как есть.
      patch[c.iikoCategory] = next === "" || next === c.iikoCategory ? null : next;
    }
    if (Object.keys(patch).length === 0) return;
    setBusy(true);
    setErr("");
    setSaved(false);
    const r = await save(patch);
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    await reload();
  }

  if (categories === null) {
    return err ? <ErrorNote text={err} /> : <Spinner />;
  }

  if (categories.length === 0) {
    return <Empty text="Касса пока не прислала категорий — обновите каталог из iiko" />;
  }

  return (
    <div className="space-y-3">
      <p className="text-[13px] leading-relaxed text-[var(--op-muted)]">
        Слева — категория учёта, как её прислала касса, и число карточек в ней. Справа — куда она
        попадёт на складе KLAIT при следующей синхронизации каталога. По умолчанию — как в кассе;
        выберите уже заведённую категорию или впишите новую.
      </p>
      {err && <ErrorNote text={err} />}
      <div className="max-h-[420px] space-y-1.5 overflow-y-auto">
        {categories.map((c) => {
          const value = draft[c.iikoCategory] ?? c.mappedTo;
          const changed = value.trim() !== c.mappedTo;
          return (
            <div
              key={c.iikoCategory}
              className="grid items-center gap-2 rounded-2xl bg-[var(--op-surface-2)] px-3 py-2 sm:grid-cols-[minmax(0,1fr)_16px_minmax(0,240px)]"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] text-[var(--op-fg)]">{c.iikoCategory}</div>
                <div className="text-[11px] text-[var(--op-faint)]">
                  {c.count} {plural(c.count, "карточка", "карточки", "карточек")}
                </div>
              </div>
              <span className="hidden text-center text-[var(--op-faint)] sm:block" aria-hidden>
                →
              </span>
              <div>
                <input
                  className={field}
                  list="iiko-category-map-options"
                  value={value}
                  disabled={!canEdit || busy}
                  onChange={(e) => setDraft((d) => ({ ...d, [c.iikoCategory]: e.target.value }))}
                  placeholder={c.iikoCategory}
                  aria-label={`Категория KLAIT для «${c.iikoCategory}»`}
                />
                {changed && <span className="mt-0.5 block text-[11px] text-[var(--op-accent)]">изменено</span>}
              </div>
            </div>
          );
        })}
      </div>
      <datalist id="iiko-category-map-options">
        {klaitCategories.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <button className={bPrimary} disabled={busy || !dirty} onClick={() => void onSave()}>
            {busy ? "Сохраняю…" : "Сохранить"}
          </button>
          {saved && <OkNote text="Сохранено — перенесётся на склад с ближайшей синхронизацией каталога («Обновить каталог из iiko»)." />}
        </div>
      )}
    </div>
  );
}
